import React, {useEffect, useState} from "react";
import {StyledOrderDetails} from "./OrderDetails.style";
import modalCloseIcon from "../../../../assets/close-icon.svg";
import personIcon from "../../../../assets/LiveOrders/personIcon2.svg";
import phoneIcon from "../../../../assets/LiveOrders/phoneIcon2.svg";
import deliveryIcon from "../../../../assets/LiveOrders/deliveryIcon.svg";
import paymentIcon from "../../../../assets/LiveOrders/paymentIcon.svg";
import receiptIcon from "../../../../assets/LiveOrders/receiptIcon.svg";
import copyIcon from "../../../../assets/LiveOrders/copyIcon.svg";
import {LuClock, LuCheck, LuX} from "react-icons/lu";
import Button from "components/molecules/Button";
import QRCode from "react-qr-code";
import Toast from "components/molecules/Toast";
import useTimeAgo from "hooks/useTimeAgo";
import thermalPrinterService from "services/thermalPrinterService";
import orderService from "services/orderService";

const OrderDetails = ({detailsSidebar, closeSidebar, data, rawOrderData, storeName, onCancelOrder, isRejecting, onRedispatchSuccess}) => {
    const timeAgo = useTimeAgo(data?.createdAt);
    const [isPrinting, setIsPrinting] = useState(false);
    const [isRedispatching, setIsRedispatching] = useState(false);

    const handlePrintReceipt = async () => {
        if (isPrinting) return;
        setIsPrinting(true);
        try {
            await thermalPrinterService.printReceipt(data, rawOrderData, storeName || "Restaurant");
            Toast({
                message: "Receipt printed successfully!",
                type: "success",
            });
        } catch (error) {
            console.error("Print failed:", error);
            Toast({
                message: error.message || "Failed to print receipt. Make sure the printer is connected.",
                type: "error",
            });
        } finally {
            setIsPrinting(false);
        }
    };
    const formatOrderTime = timestamp => {
        if (!timestamp) return "N/A";
        const date = new Date(timestamp);
        return date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const calculateItemPrice = (item, rawItem) => {
        if (rawItem?.lineTotalInCents) {
            return `$${(rawItem.lineTotalInCents / 100).toFixed(2)}`;
        }
        if (rawItem?.menuItem?.price) {
            const unitPrice = rawItem.unitPriceInCents ? rawItem.unitPriceInCents / 100 : rawItem.menuItem.price;
            return `$${(unitPrice * item.quantity).toFixed(2)}`;
        }
        // Fallback calculation if no individual price available
        const totalPrice = parseFloat(data?.price || 0);
        const totalItems = data?.items?.reduce((sum, i) => sum + i.quantity, 0) || 1;
        const avgPrice = totalPrice / totalItems;
        return `$${(avgPrice * item.quantity).toFixed(2)}`;
    };

    const calculatePricing = () => {
        const subtotal = rawOrderData?.pricing?.subTotal
            ? (rawOrderData.pricing.subTotal / 100).toFixed(2)
            : rawOrderData?.subTotalInCents
              ? (rawOrderData.subTotalInCents / 100).toFixed(2)
              : "0.00";

        const deliveryFee = rawOrderData?.pricing?.deliveryFee
            ? (rawOrderData.pricing.deliveryFee / 100).toFixed(2)
            : rawOrderData?.deliveryFeeInCents
              ? (rawOrderData.deliveryFeeInCents / 100).toFixed(2)
              : "0.00";

        const tax = rawOrderData?.pricing?.tax
            ? (rawOrderData.pricing.tax / 100).toFixed(2)
            : rawOrderData?.taxInCents
              ? (rawOrderData.taxInCents / 100).toFixed(2)
              : "0.00";

        const discount = rawOrderData?.pricing?.discount
            ? (rawOrderData.pricing.discount / 100).toFixed(2)
            : rawOrderData?.discountInCents
              ? (rawOrderData.discountInCents / 100).toFixed(2)
              : "0.00";

        const tip = rawOrderData?.pricing?.tip
            ? (rawOrderData.pricing.tip / 100).toFixed(2)
            : rawOrderData?.tipAmount
              ? (rawOrderData.tipAmount / 100).toFixed(2)
              : "0.00";

        const total = rawOrderData?.pricing?.total
            ? (rawOrderData.pricing.total / 100).toFixed(2)
            : rawOrderData?.totalAmountInCents
              ? (rawOrderData.totalAmountInCents / 100).toFixed(2)
              : parseFloat(data?.price || 0).toFixed(2);

        return {
            subtotal,
            deliveryFee,
            tax,
            discount,
            tip,
            total,
        };
    };

    const pricing = calculatePricing();
    const orderStatus = e => {
        switch (e) {
            case "preparing":
                return <span className="status preparing">Preparing</span>;
            case "ready":
                return <span className="status ready">Ready</span>;
            case "ready_for_pickup":
                return <span className="status ready">Ready for Pickup</span>;
            case "accepted":
                return <span className="status accepted">Driver Accepted</span>;
            case "out_for_delivery":
                return <span className="status out_for_delivery">Out for Delivery</span>;
            case "ownership_taken":
                return <span className="status out_for_delivery">Out for Delivery</span>;
            case "ownership taken":
                return <span className="status out_for_delivery">Out for Delivery</span>;
            case "completed":
                return <span className="status completed">Delivered</span>;
            case "delivered":
                return <span className="status completed">Delivered</span>;
            case "new_order":
                return <span className="status pending">New Order</span>;
            case "cancelled":
                return <span className="status cancelled">Cancelled</span>;
            default:
                return <span className="status pending">Pending</span>;
        }
    };

    useEffect(() => {
        if (detailsSidebar) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [detailsSidebar]);

    const copyText = () => {
        const text = document.getElementById("code").innerText;
        navigator.clipboard
            .writeText(text)
            .then(() => {
                Toast({
                    message: "Code copied to clipboard!",
                    type: "success",
                });
            })
            .catch(err => {
                console.error("Failed to copy code: ", err);
            });
    };
    return (
        <StyledOrderDetails $isOpen={detailsSidebar}>
            <div className="overlay" onClick={closeSidebar}></div>
            <div className="sidebar">
                <button type="button" className="closer" onClick={closeSidebar} tabIndex={0}>
                    <img src={modalCloseIcon} alt="modalCloseIcon" />
                </button>
                <div className="card-head">
                    <div className="count-wrap">
                        <strong className="count">
                            # {data?.id}{" "}
                            <span
                                className="dot"
                                style={{
                                    backgroundColor: `${
                                        data?.orderFrom === "Foodie" ? "rgba(44, 185, 255, 1)" : "rgba(133, 39, 196, 1)"
                                    }`,
                                }}></span>
                        </strong>
                        <span className="time">{timeAgo}</span>
                    </div>
                    {orderStatus(data?.status)}
                </div>
                <ul className="items-list">
                    {data?.items?.map((item, index) => (
                        <li key={index}>
                            <span className="item">
                                {item?.title} × {item?.quantity}
                            </span>
                            <strong className="item-price">
                                {calculateItemPrice(item, rawOrderData?.items?.[index])}
                            </strong>
                        </li>
                    ))}
                </ul>
                {(rawOrderData?.notes ||
                    rawOrderData?.items?.some(item => item.notes) ||
                    data?.items?.some(item => item.addOn)) && (
                    <div className="special-instructions">
                        <strong className="title">Special Instructions:</strong>
                        <span className="desc">
                            {rawOrderData?.notes ||
                                rawOrderData?.items
                                    ?.filter(item => item.notes)
                                    .map(item => item.notes)
                                    .join(", ") ||
                                data?.items
                                    ?.filter(item => item.addOn)
                                    .map(item => item.addOn)
                                    .join(", ") ||
                                "No special instructions"}
                        </span>
                    </div>
                )}
                <div className="separator"></div>
                <div className="details-wrap">
                    <div className="details-title">
                        <img src={personIcon} alt="icon" />
                        <strong>Customer Info</strong>
                    </div>
                    <ul className="details-list">
                        <li className="name">
                            <strong>
                                {rawOrderData?.integrations?.meta?.customer_f_name &&
                                rawOrderData?.integrations?.meta?.customer_l_name
                                    ? `${rawOrderData.integrations.meta.customer_f_name} ${rawOrderData.integrations.meta.customer_l_name}`
                                    : rawOrderData?.roundtripPayload?.customer_f_name &&
                                        rawOrderData?.roundtripPayload?.customer_l_name
                                      ? `${rawOrderData.roundtripPayload.customer_f_name} ${rawOrderData.roundtripPayload.customer_l_name}`
                                      : rawOrderData?.customer?.profile?.first_name ||
                                        rawOrderData?.guestInfo?.name ||
                                        data?.name ||
                                        "N/A"}
                            </strong>
                            <img src={phoneIcon} alt="icon" width={24} height={24} />
                        </li>
                        <li>
                            <span>
                                {rawOrderData?.integrations?.meta?.customer_phone ||
                                    rawOrderData?.roundtripPayload?.customer_phone ||
                                    rawOrderData?.customer?.profile?.phone ||
                                    rawOrderData?.guestInfo?.phone ||
                                    data?.phone ||
                                    "N/A"}
                            </span>
                        </li>
                        <li>
                            <span>
                                {rawOrderData?.integrations?.meta?.customer_email ||
                                    rawOrderData?.customer?.email ||
                                    rawOrderData?.customer?.profile?.email ||
                                    rawOrderData?.guestInfo?.email ||
                                    ""}
                            </span>
                        </li>
                    </ul>
                </div>
                <div className="separator"></div>
                {data?.orderType === "delivery" && (
                    <div className="delivery-wrap">
                        <div className="delivery-title">
                            <img src={deliveryIcon} alt="icon" />
                            <strong>Delivery Information</strong>
                        </div>
                        <p className="location">
                            {rawOrderData?.fulfillment?.address?.street_address ||
                                (rawOrderData?.fulfillment?.address?.city &&
                                rawOrderData?.fulfillment?.address?.state &&
                                rawOrderData?.fulfillment?.address?.postal_code
                                    ? `${rawOrderData.fulfillment.address.street_address || ""} ${
                                          rawOrderData.fulfillment.address.city || ""
                                      }, ${rawOrderData.fulfillment.address.state || ""} ${
                                          rawOrderData.fulfillment.address.postal_code || ""
                                      }`.trim()
                                    : "") ||
                                rawOrderData?.integrations?.meta?.drop_off_location ||
                                rawOrderData?.roundtripPayload?.drop_off_location ||
                                rawOrderData?.store?.address?.street_address ||
                                data?.address ||
                                "N/A"}
                        </p>

                        {data?.status === "accepted" && (
                            <div className="rider-wrap">
                                <div>
                                    <h2 className="rider-name">
                                        Rider: {data?.deliveryInfo?.driverName || "Assigned"}
                                    </h2>
                                    <span className="rider-time">Status: Driver Accepted</span>
                                </div>
                                {data?.deliveryInfo?.driverPhone && (
                                    <button
                                        className="btn"
                                        onClick={() => window.open(`tel:${data.deliveryInfo.driverPhone}`)}>
                                        Call Rider
                                    </button>
                                )}
                            </div>
                        )}

                        {(data?.status === "out_for_delivery" || data?.status === "ownership_taken") &&
                            data?.deliveryInfo && (
                                <div className="rider-wrap">
                                    <div>
                                        <h2 className="rider-name">Rider: {data.deliveryInfo.driverName}</h2>
                                        <span className="rider-time">Status: Out for Delivery</span>
                                    </div>
                                    {data.deliveryInfo.driverPhone && (
                                        <button
                                            className="btn"
                                            onClick={() => window.open(`tel:${data.deliveryInfo.driverPhone}`)}>
                                            Call Rider
                                        </button>
                                    )}
                                </div>
                            )}

                        {(data?.status === "out_for_delivery" || data?.status === "ownership_taken") &&
                            !data?.deliveryInfo && (
                                <div className="rider-wrap">
                                    <div>
                                        <h2 className="rider-name">Rider: Assigned</h2>
                                        <span className="rider-time">Status: Out for Delivery</span>
                                    </div>
                                </div>
                            )}

                        {(data?.status === "completed" || data?.status === "delivered") && (
                            <div className="rider-wrap">
                                <div>
                                    <span className="rider-time">Status: Delivered</span>
                                </div>
                            </div>
                        )}

                        {data?.status === "ready_for_pickup" && !data?.deliveryInfo && data?.driverCancellation?.type !== "post_pickup" && (
                            <div className="rider-wrap">
                                <div>
                                    <h2 className="rider-name">Rider: Not assigned yet</h2>
                                    <span className="rider-time">Status: Waiting for rider assignment</span>
                                </div>
                            </div>
                        )}

                        {/* Post-pickup cancellation context */}
                        {data?.driverCancellation?.type === "post_pickup" && (
                            <div
                                style={{
                                    background: "#FFF8E1",
                                    border: "1px solid #FFB300",
                                    borderRadius: "8px",
                                    padding: "12px",
                                    marginTop: "12px",
                                }}>
                                <strong style={{color: "#E65100", fontSize: "13px"}}>⚠️ Driver Cancelled After Pickup</strong>
                                <ul style={{margin: "8px 0 0", padding: "0 0 0 16px", fontSize: "12px", color: "#5D4037", lineHeight: "1.6"}}>
                                    {data.driverCancellation.driverName && (
                                        <li>Driver: {data.driverCancellation.driverName}</li>
                                    )}
                                    {data.driverCancellation.reason && (
                                        <li>Reason: {data.driverCancellation.reason.replace(/_/g, " ")}</li>
                                    )}
                                    <li>Food must be returned to the restaurant</li>
                                    <li>A new driver is being assigned automatically</li>
                                </ul>
                                <p style={{margin: "8px 0 0", fontSize: "11px", color: "#8D6E63", fontStyle: "italic"}}>
                                    Once the food is returned, inspect it. If compromised, cancel the order below.
                                </p>
                            </div>
                        )}

                        {/* Default fallback for other statuses */}
                        {![
                            "accepted",
                            "out_for_delivery",
                            "ownership_taken",
                            "completed",
                            "delivered",
                            "ready_for_pickup",
                        ].includes(data?.status) && (
                            <div className="rider-wrap">
                                <div>
                                    <h2 className="rider-name">Rider: Not assigned yet</h2>
                                    <span className="rider-time">Status: Waiting for rider assignment</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {data?.pickupCode !== "N/A" && (
                    <div className="qr-code-wrap">
                        <div>
                            <h2 className="qr-title">Order Handover</h2>
                            <span className="qr-desc">Pickup Code</span>
                            <div className="code-wrap">
                                <strong className="code" id="code">
                                    {data?.pickupCode && data?.pickupCode !== "N/A"
                                        ? data.pickupCode
                                        : data?.id || "0000"}
                                </strong>
                                <img src={copyIcon} alt="copyIcon" onClick={() => copyText()} />
                            </div>
                        </div>

                        <div className="qr-code">
                            <QRCode
                                size={80}
                                value={
                                    data?.bookingCode && data?.bookingCode !== "N/A"
                                        ? data.bookingCode
                                        : data?.id || "0000"
                                }
                                viewBox={`0 0 80 80`}
                                bgColor="rgba(236, 236, 236, 1)"
                            />
                        </div>
                    </div>
                )}
                <div className="payment-wrap">
                    {/* {(rawOrderData?.payment?.status === "cod_pending" ||
                        rawOrderData?.paymentStatus === "cod_pending") && (
                        <div className="cod-banner py-2">
                            <span>💵 Collect Cash upon Delivery</span>
                        </div>
                    )} */}
                    <div className="payment-title">
                        <img src={paymentIcon} alt="icon" />
                        <strong>Payment Information</strong>
                    </div>

                    <ul className="pricing-list">
                        <li>
                            <span className="title">Subtotal</span>
                            <strong className="price">${pricing.subtotal}</strong>
                        </li>
                        {data?.orderType === "delivery" && parseFloat(pricing.deliveryFee) > 0 && (
                            <li>
                                <span className="title">Delivery Fee</span>
                                <strong className="price">${pricing.deliveryFee}</strong>
                            </li>
                        )}
                        {parseFloat(pricing.tax) > 0 && (
                            <li>
                                <span className="title">Tax</span>
                                <strong className="price">${pricing.tax}</strong>
                            </li>
                        )}
                        {parseFloat(pricing.discount) > 0 && (
                            <li>
                                <span className="title">Discount</span>
                                <strong className="price">-${pricing.discount}</strong>
                            </li>
                        )}
                        {parseFloat(pricing.tip) > 0 && (
                            <li>
                                <span className="title">Tip</span>
                                <strong className="price">${pricing.tip}</strong>
                            </li>
                        )}
                        <div className="separator"></div>
                        <li className="total">
                            <span className="title">Total</span>
                            <strong className="price">${pricing.total}</strong>
                        </li>
                        <li>
                            <span className="title" style={{color: "rgba(143, 143, 143, 1)"}}>
                                Payment:{" "}
                                {rawOrderData?.payment?.method === "credit_card" ||
                                rawOrderData?.paymentMethod === "credit_card"
                                    ? "Credit Card"
                                    : rawOrderData?.payment?.method === "debit_card" ||
                                        rawOrderData?.paymentMethod === "debit_card"
                                      ? "Debit Card"
                                      : rawOrderData?.payment?.method === "cash" ||
                                          rawOrderData?.paymentMethod === "cash"
                                        ? "Cash"
                                        : rawOrderData?.payment?.method === "cash_on_delivery" ||
                                            rawOrderData?.paymentMethod === "cash_on_delivery"
                                          ? "Cash on Delivery"
                                          : rawOrderData?.payment?.method || rawOrderData?.paymentMethod || "N/A"}
                            </span>
                            {(rawOrderData?.payment?.status === "cod_pending" ||
                                rawOrderData?.paymentStatus === "cod_pending") &&
                                data?.status?.toLowerCase() !== "delivered" && (
                                <span className="payment-status-badge payment-pending">Cash Pending</span>
                            )}
                            {((rawOrderData?.payment?.status === "collected" ||
                                rawOrderData?.paymentStatus === "collected") ||
                                ((rawOrderData?.payment?.status === "cod_pending" ||
                                    rawOrderData?.paymentStatus === "cod_pending") &&
                                    data?.status?.toLowerCase() === "delivered")) && (
                                <span className="payment-status-badge payment-collected">Cash Collected</span>
                            )}
                        </li>
                    </ul>
                </div>
                <div className="separator"></div>
                <div className="order-wrap">
                    <div className="order-title">
                        <LuClock color="var(--text-color)" size={16} />
                        <strong>Order Timeline</strong>
                    </div>
                    <div className="order-placed">
                        <span>Order Placed</span>
                        <span>{formatOrderTime(data?.createdAt)}</span>
                    </div>
                </div>
                <div className="btn-holder" style={{flexDirection: "column", alignItems: "center", gap: "16px"}}>
                    {data?.status !== "cancelled" && (
                        <Button className="print-btn" variant="completed" onClick={handlePrintReceipt} disabled={isPrinting}>
                            <img src={receiptIcon} alt="icon" />
                            {isPrinting ? "Printing..." : "Print Receipt"}
                        </Button>
                    )}
                    {/* Post-pickup cancellation: two action buttons */}
                    {data?.driverCancellation?.type === "post_pickup" && data?.status === "ready_for_pickup" && (
                        <div style={{width: "100%", maxWidth: "319px", display: "flex", flexDirection: "column", gap: "8px", borderTop: "1px solid #E9E9E9", paddingTop: "16px"}}>
                            <p style={{fontSize: "12px", color: "#666", textAlign: "center", margin: "0 0 4px 0"}}>
                                Has the food been returned and inspected?
                            </p>
                            <Button
                                variant="ready"
                                disabled={isRedispatching}
                                style={{width: "100%"}}
                                onClick={async () => {
                                    setIsRedispatching(true);
                                    try {
                                        const response = await orderService.redispatchOrder(rawOrderData?._id);
                                        Toast({message: "New driver is being dispatched!", type: "success"});
                                        closeSidebar();
                                        // Update state instantly without reloading
                                        if (onRedispatchSuccess && response?.order) {
                                            onRedispatchSuccess(response.order);
                                        }
                                    } catch (err) {
                                        Toast({message: err.message || "Failed to redispatch", type: "error"});
                                    } finally {
                                        setIsRedispatching(false);
                                    }
                                }}>
                                <LuCheck size={18} strokeWidth={3} />
                                {isRedispatching ? "Dispatching..." : "Food OK — Send New Driver"}
                            </Button>
                            {onCancelOrder && (
                                <Button
                                    danger={true}
                                    onClick={onCancelOrder}
                                    disabled={isRejecting}
                                    style={{width: "100%"}}>
                                    <LuX size={18} strokeWidth={3} />
                                    {isRejecting ? "Cancelling..." : "Cancel Order — Food Compromised"}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </StyledOrderDetails>
    );
};

export default OrderDetails;
